import { Outlet, useLocation } from 'react-router-dom';
import Header from './Header';
import Sidebar from './Sidebar';
import BottomTabBar from './BottomTabBar';
import BottomSlot from './BottomSlot';
import CreditBalanceBanner from './CreditBalanceBanner';
import NotificationContactBanner from './NotificationContactBanner';
import { useBriefing } from '@/lib/hooks/useBriefing';
import { PushDeviceSync } from '@/lib/push/PushDeviceSync';

/**
 * Story 24.7b — the flow column (DESIGN.md › Layout & Spacing, E1). At every
 * width the root is ONE viewport-bounded flex column (`h-viewport`: `100dvh`
 * minus the on-screen keyboard's `--kb-inset`):
 *
 *   row (sidebar + header + credit banner + contact banner + `<main>`) /
 *   bottom slot / tab bar
 *
 * `<main data-app-scroll>` is the ONLY scroll area. Nothing overlays it, so a
 * focused element can never sit under the tab bar or the toast (WCAG 2.4.11).
 * The tab bar and the bottom slot are real rows, not sticky overlays. The
 * window itself never scrolls on an authenticated route: use
 * `scrollAppContentToTop` (`src/lib/ui/appScroll.ts`), never
 * `window.scrollTo`.
 *
 * The top safe-area pad (`pt-safe-top`) is applied ONCE, here on the root, as
 * the first chrome bar's pad. A bar added later must not add it again.
 *
 * `min-h-0` on the row and the column lets them shrink below their content
 * (a flex child's `min-height: auto` floor is otherwise its content).
 *
 * FULL_HEIGHT_ROUTES render a self-contained "app surface" that owns its own
 * internal scroll: `<UnifiedShell>` (`/chat`) roots on `h-full` +
 * `overflow-hidden` with panels that scroll, and `/tasks` (Epic 6 story 6.1)
 * is a bounded flex row of a scrolling list lens and a scrolling detail
 * drawer. `h-full` resolves only against a DEFINITE height, which the bounded
 * `<main>` gives them; there `<main>` is `overflow-hidden`, so the inner
 * panels clamp and the composer stays at the bottom.
 *
 * Every other route scrolls inside `<main>` (`overflow-y-auto`), which also
 * carries `data-scroll-container`, so the desktop toast's `--toast-inset`
 * padding (`src/index.css`) applies to it.
 *
 * Story 24.5 removed the in-app footer from every route (DESIGN.md A2) —
 * login, signup and public pages keep their own and size with `min-h-dvh`.
 *
 * Story 24.18 — the ONE chrome reader of the briefing. `useBriefing()` is
 * mounted here and its `waitingCount` is passed down as a prop to `Sidebar`
 * and `BottomTabBar`, which draw it on Tasks and never call the hook
 * themselves. The key is the shared `['briefing', tenantId, userId]`, so
 * `/chat`'s own readers (the agent tree, Today) dedupe into the same request.
 *
 * Story 29.2 — `unreadCount` (conversations with unread replies) rides next
 * to `waitingCount` to the same two components, which draw it on Chats.
 *
 * Story 30.3 — `PushDeviceSync` (render-nothing) registers the push worker
 * and runs the once-per-shell device sync (VAPID rotation, unlisted
 * subscription). It renders nothing in a browser that cannot receive push.
 */
const FULL_HEIGHT_ROUTES = ['/chat', '/tasks'];

export default function MainLayout() {
  const { pathname } = useLocation();
  const { waitingCount, unreadCount } = useBriefing();
  const fullHeight = FULL_HEIGHT_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  );

  return (
    <div className="h-viewport pt-safe-top flex flex-col font-body">
      <PushDeviceSync />
      <div className="flex flex-1 min-h-0">
        <Sidebar waitingCount={waitingCount} unreadCount={unreadCount} />

        <div className="flex-1 flex flex-col lg:pl-0 min-w-0 min-h-0">
          <Header />

          {/* Story 16.4 — low / exhausted credit banner (renders nothing when ok). */}
          <CreditBalanceBanner />

          {/* Story 29.3 — owner-only "Choose who gets notifications" until a
              tenant contact is set (renders nothing otherwise). */}
          <NotificationContactBanner />

          <main
            data-app-scroll
            data-scroll-container={fullHeight ? undefined : ''}
            className={`flex-1 min-h-0 bg-canvas ${fullHeight ? 'overflow-hidden' : 'overflow-y-auto'}`}
          >
            <Outlet />
          </main>
        </div>
      </div>

      {/* Story 24.7b — below 1024px the toast's reserved height (renders
          nothing while a chat composer holds the reservation). */}
      <BottomSlot />

      {/* Story 24.7 — below 1024px the phone tab bar is the LAST row of this
          flow column (in flow, never fixed). It renders `lg:hidden`, so the
          desktop chrome above is untouched. */}
      <BottomTabBar waitingCount={waitingCount} unreadCount={unreadCount} />
    </div>
  );
}
